/**
 * Who is on the team, what they can do, and how to change it.
 *
 * ROLES ARE CAPABILITY BUNDLES, not hardcoded checks. The server ships five
 * templates and an org may adjust what each one grants; this page shows the
 * EFFECTIVE set, so a customised role reads as what it actually does rather
 * than what its name suggests.
 *
 * DISABLE IS OFFERED BEFORE DELETE, and deliberately listed first. Removing a
 * member throws away the record of what they did and who added them; disabling
 * keeps it while making the account unusable immediately. Delete is for a
 * mistake, not for someone who left.
 */

import { Fragment, useCallback, useEffect, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { auth, functions } from '../firebase';
import { ClientScope } from './ClientScope';

interface Member {
  displayName?: string | null;
  uid: string;
  email: string | null;
  role: string;
  disabled?: boolean;
  addedBy?: string | null;
  /** From the AUTH record, not the membership doc. */
  state?: 'active' | 'invited' | 'disabled';
  lastSignIn?: string | null;
  emailVerified?: boolean;
  mfaEnrolled?: boolean;
  /** Absent means unrestricted - the same default the rules use. */
  viewAllClients?: boolean;
  scopeMode?: 'all' | 'include' | 'exclude';
  clientIds?: string[];
}

interface RoleInfo {
  role: string;
  label: string;
  description: string;
  template: string[];
  effective: string[];
  locked: boolean;
}

interface Catalog {
  capabilities: string[];
  roles: RoleInfo[];
  overrides: Record<string, Record<string, boolean> | undefined>;
  assignable: string[];
}

// CAP_LABEL moved to roles.tsx with the editor it existed for.

/**
 * Where this person actually is.
 *
 * INVITED is the state that matters and the one a plain member list cannot
 * show: the account exists, it counts as a member, and nobody has ever signed
 * in to it. Someone stranded on a lost invitation looks identical to a working
 * colleague unless it is said out loud.
 */
function StatePill({ m }: { m: Member }) {
  const state = m.state ?? (m.disabled ? 'disabled' : 'active');
  if (state === 'disabled') {
    return <span className="pill pill-bad" title="Cannot sign in">Disabled</span>;
  }
  if (state === 'invited') {
    return (
      <span className="pill pill-warn" title="Invited, but has never signed in">
        Invited
      </span>
    );
  }
  return (
    <span
      className="pill pill-ok"
      title={m.lastSignIn ? `Last signed in ${new Date(m.lastSignIn).toLocaleString()}` : 'Has signed in'}
    >
      Active
    </span>
  );
}

export function TeamMembers({ orgId, canManage }: { orgId: string | null; canManage: boolean }) {
  const [members, setMembers] = useState<Member[] | null>(null);
  const [cat, setCat] = useState<Catalog | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  /** Which member's client access is open for editing. */
  const [scopeFor, setScopeFor] = useState<string | null>(null);

  const [email, setEmail] = useState('');
  const [role, setRole] = useState('viewer');

  const load = useCallback(async () => {
    if (!orgId) return;
    try {
      const t = await httpsCallable(functions, 'orgTeam')({ orgId });
      setMembers(((t.data as { members: Member[] }).members ?? []).slice());
      if (canManage) {
        const c = await httpsCallable(functions, 'getRoleCatalog')({ orgId });
        setCat(c.data as Catalog);
      }
    } catch (err) {
      setMsg({ ok: false, text: err instanceof Error ? err.message : 'Could not load the team.' });
      setMembers([]);
    }
  }, [orgId, canManage]);

  useEffect(() => { void load(); }, [load]);

  // Which row is being renamed, and the text in it. One at a time on purpose:
  // two open editors invite saving the wrong one.
  const [editing, setEditing] = useState<string | null>(null);
  const [nameDraft, setNameDraft] = useState('');

  if (!orgId) return null;

  async function saveName(uid: string) {
    const wanted = nameDraft.trim();
    await run('name-' + uid, async () => {
      await httpsCallable(functions, 'setMemberName')({ orgId, uid, displayName: wanted });
      setEditing(null);
      return wanted
        ? `Saved. ${wanted} is how they will appear on invitations and notes.`
        : 'Name cleared. They will show as their email address.';
    });
  }

  async function run(key: string, fn: () => Promise<string>) {
    setBusy(key); setMsg(null);
    try {
      setMsg({ ok: true, text: await fn() });
      await load();
    } catch (err) {
      setMsg({ ok: false, text: err instanceof Error ? err.message : 'That did not work.' });
    } finally { setBusy(null); }
  }

  const invite = () => run('invite', async () => {
    const r = await httpsCallable(functions, 'inviteMember')({ orgId, email: email.trim(), role });
    const d = r.data as { created: boolean; emailed: boolean; emailError: string | null };
    setEmail('');
    // "Added but never told" must not read as plain success.
    if (!d.emailed) return `Added, but the invitation email failed: ${d.emailError}`;
    return d.created ? `Invited ${email.trim()}.` : `${email.trim()} already had an account and has been added.`;
  });

  const labelFor = (r: string) => cat?.roles.find((x) => x.role === r)?.label ?? r;
  const assignable = cat?.assignable ?? [];

  return (
    <div className="settings">
      <section className="panel">
        <h2 className="panel-title">
          Team members
          {/*
            * REFRESH, because the two things this page is watched for -
            * "has she accepted yet" and "has he set up 2FA" - both live on the
            * Auth record and change without anything in this app happening.
            * Nothing pushes those, so the list is only ever as current as the
            * last load, and reloading the whole dashboard to check is a poor
            * answer to a question people ask several times an hour while
            * onboarding somebody.
            */}
          <button
            className="btn-link"
            style={{ marginLeft: 10, fontSize: 11, fontWeight: 500 }}
            disabled={busy !== null}
            title="Re-read the team from the server"
            onClick={() => void run('refresh', async () => {
              await load();
              return 'Refreshed.';
            })}
          >
            {busy === 'refresh' ? 'Refreshing...' : 'Refresh'}
          </button>
        </h2>
        <p className="panel-note">
          People who can sign in to this organisation. An invitation emails a one-time sign-in link -
          no password is ever sent, and the recipient sets their own on arrival.
        </p>

        {msg && <div className={'banner ' + (msg.ok ? 'banner-ok' : 'banner-error')}>{msg.text}</div>}

        {canManage && (
          <div className="row-form" style={{ marginBottom: 14 }}>
            <label className="field" style={{ flex: 2 }}>
              <span className="field-label">Invite by email</span>
              <input
                className="field-input" type="email" placeholder="someone@example.com"
                value={email} onChange={(e) => setEmail(e.target.value)}
              />
            </label>
            <label className="field">
              <span className="field-label">Role</span>
              <select className="field-input" value={role} onChange={(e) => setRole(e.target.value)}>
                {assignable.map((r) => (
                  <option key={r} value={r}>{labelFor(r)}</option>
                ))}
              </select>
            </label>
            <button
              className="btn btn-primary" style={{ alignSelf: 'flex-end' }}
              disabled={busy !== null || !email.trim()} onClick={invite}
            >
              {busy === 'invite' ? 'Inviting...' : 'Invite'}
            </button>
          </div>
        )}

        {members === null ? (
          <div className="empty">Loading...</div>
        ) : members.length === 0 ? (
          <div className="empty">Nobody has been added yet.</div>
        ) : (
          <table className="mini-table">
            <thead>
              <tr><th>Person</th><th>Role</th><th /></tr>
            </thead>
            <tbody>
              {members.map((m) => {
              // The server refuses both of these. Computing them here as well
              // is not duplication for its own sake: a button that explains
              // why it is unavailable beats one that fails after the click,
              // and "Delete" sitting live on your own row is a misclick
              // waiting to happen.
              const isSelf = m.uid === auth.currentUser?.uid;
              const lastOwner = m.role === 'owner'
                && members.filter((o) => o.role === 'owner' && !o.disabled).length <= 1;
              const cannotRemove = isSelf
                ? 'You cannot remove your own membership'
                : lastOwner ? 'The only owner cannot be removed - make somebody else an owner first' : '';
              const cannotDisable = isSelf
                ? 'You cannot disable your own account'
                : lastOwner ? 'The only owner cannot be disabled' : '';
              return (
                // Two sibling rows now: the member, and its access editor.
                <Fragment key={m.uid}>
                <tr className={m.disabled ? 'row-disabled' : undefined}>
                  <td>
                    {/* Name first when there is one, with the address beneath. A list of
                          raw email addresses is readable; a list of people is
                          scannable, and this is the page where "who is that"
                          gets asked most. */}
                      {editing === m.uid ? (
                          /* Inline rather than a dialog: it is one short field,
                             and the row it belongs to is the context. */
                          <div className="acct-row" style={{ marginBottom: 6 }}>
                            <input
                              className="acct-input"
                              autoFocus
                              placeholder={m.email ?? 'Their name'}
                              value={nameDraft}
                              maxLength={80}
                              disabled={busy !== null}
                              onChange={(e) => setNameDraft(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') void saveName(m.uid);
                                if (e.key === 'Escape') setEditing(null);
                              }}
                            />
                            <button
                              className="btn-link" disabled={busy !== null}
                              onClick={() => void saveName(m.uid)}
                            >
                              Save
                            </button>
                            <button className="btn-link" onClick={() => setEditing(null)}>Cancel</button>
                          </div>
                        ) : (
                          <div className="cell-name">
                            {m.displayName || m.email || m.uid}
                            {canManage && (
                              <button
                                className="btn-link"
                                style={{ marginLeft: 8, fontSize: 11 }}
                                title={m.displayName ? 'Change this name' : 'Add a name for this person'}
                                onClick={() => { setEditing(m.uid); setNameDraft(m.displayName ?? ''); }}
                              >
                                {m.displayName ? 'rename' : 'add name'}
                              </button>
                            )}
                          </div>
                        )}
                      {m.displayName && m.email && (
                        <div className="dim mono" style={{ fontSize: 11 }}>{m.email}</div>
                      )}
                    <div className="pill-row">
                      <StatePill m={m} />
                      {/* Second factor is a separate fact from account state -
                          an active account with no 2FA is the one worth
                          seeing, so it gets its own pill rather than a
                          footnote on the first. */}
                      {m.mfaEnrolled
                        ? <span className="pill pill-ok" title="Authenticator app enrolled">2FA on</span>
                        : <span className="pill pill-off" title="No second factor - password only">2FA off</span>}
                      {/* Visible at a glance, not only once somebody opens the
                          editor - "who can see what" is the question this page
                          exists to answer. */}
                      {m.scopeMode === 'exclude' ? (
                        <span className="pill pill-warn"
                              title="Sees everything except these. Clients added later are included.">
                          all but {m.clientIds?.length ?? 0}
                        </span>
                      ) : m.viewAllClients === false ? (
                        <span className="pill pill-warn"
                              title="Sees only the clients assigned to them. New clients are not added automatically.">
                          {m.clientIds?.length ?? 0} client{(m.clientIds?.length ?? 0) === 1 ? '' : 's'}
                        </span>
                      ) : null}
                    </div>
                  </td>
                  <td>
                    {canManage && m.role !== 'owner' && assignable.length ? (
                      <select
                        className="field-input" value={m.role} disabled={busy !== null}
                        onChange={(e) => void run('role' + m.uid, async () => {
                          await httpsCallable(functions, 'setMemberRole')({
                            orgId, email: m.email, role: e.target.value,
                          });
                          return `${m.email} is now ${labelFor(e.target.value)}.`;
                        })}
                      >
                        {[...new Set([m.role, ...assignable])].map((r) => (
                          <option key={r} value={r}>{labelFor(r)}</option>
                        ))}
                      </select>
                    ) : (
                      <span className="tag">{labelFor(m.role)}</span>
                    )}
                  </td>
                  <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                    {canManage && (
                      <>
                        <button
                          className="btn-link" disabled={busy !== null}
                          onClick={() => void run('re' + m.uid, async () => {
                            const r = await httpsCallable(functions, 'resendInvite')({ orgId, uid: m.uid });
                            return `Invitation resent to ${(r.data as { sentTo: string }).sentTo}.`;
                          })}
                        >
                          Resend invite
                        </button>
                        <button
                          className="btn-link" style={{ marginLeft: 12 }}
                          disabled={busy !== null || (!m.disabled && cannotDisable !== '')}
                          title={m.disabled ? '' : cannotDisable}
                          onClick={() => void run('dis' + m.uid, async () => {
                            await httpsCallable(functions, 'setMemberDisabled')({
                              orgId, uid: m.uid, disabled: !m.disabled,
                            });
                            return m.disabled ? `${m.email} can sign in again.` : `${m.email} is disabled.`;
                          })}
                        >
                          {m.disabled ? 'Enable' : 'Disable'}
                        </button>
                        {/* The ONLY way back in for someone who lost the
                            phone holding their authenticator - Firebase TOTP
                            has no backup codes. Does not touch the password. */}
                        <button
                          className="btn-link" style={{ marginLeft: 12 }} disabled={busy !== null}
                          onClick={() => void run('mfa' + m.uid, async () => {
                            const r = await httpsCallable(functions, 'resetUserMfa')({ orgId, uid: m.uid });
                            const n = (r.data as { cleared: number }).cleared;
                            return n
                              ? `Cleared ${n} second factor(s) for ${m.email}. They can set it up again at next sign-in.`
                              : `${m.email} had no second factor enrolled.`;
                          })}
                        >
                          Reset 2FA
                        </button>
                        <button
                          className="btn-link" style={{ marginLeft: 12 }} disabled={busy !== null}
                          title="Choose which clients this person can see"
                          onClick={() => setScopeFor(scopeFor === m.uid ? null : m.uid)}
                        >
                          {scopeFor === m.uid ? 'Close access' : 'Client access'}
                        </button>
                        <button
                          className="btn-link danger" style={{ marginLeft: 12 }}
                          disabled={busy !== null || cannotRemove !== ''}
                          title={cannotRemove}
                          onClick={() => {
                            // Disable is reversible and keeps the audit trail;
                            // this is not, so it asks.
                            if (!confirm(`Remove ${m.email} from this organisation? Disabling instead keeps the record of what they did.`)) return;
                            void run('del' + m.uid, async () => {
                              await httpsCallable(functions, 'removeMember')({ orgId, uid: m.uid });
                              return `${m.email} removed.`;
                            });
                          }}
                        >
                          Delete
                        </button>
                      </>
                    )}
                  </td>
                </tr>

                {scopeFor === m.uid && (
                  <tr>
                    <td colSpan={3}>
                      <ClientScope
                        orgId={orgId} uid={m.uid} email={m.email} role={m.role}
                        mode={m.scopeMode ?? (m.viewAllClients === false ? 'include' : 'all')}
                        clientIds={m.clientIds ?? []}
                        onSaved={() => { setScopeFor(null); void load(); }}
                      />
                    </td>
                  </tr>
                )}
                </Fragment>
                );
              })}
            </tbody>
          </table>
        )}
      </section>

      {/* THE ROLE EDITOR MOVED TO ITS OWN PAGE.

          Two pages, two questions: this one is WHO is on the team, Roles is
          what the words beside their names mean. They were on one page while
          there were five fixed roles and nothing to compose; once an
          organisation could invent its own, the editor was the larger half of
          a page named after something else. */}
      {canManage && (
        <section className="panel">
          <h2 className="panel-title">Roles and permissions</h2>
          <p className="panel-note" style={{ marginTop: 0 }}>
            What each role can do &mdash; and creating your own &mdash; now lives under
            {' '}<strong>Configuration &rarr; Roles</strong>.
          </p>
        </section>
      )}
    </div>
  );
}
